import { FileText, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTags } from '../hooks/useTags';
import type { Tag } from '../types/tags';
import { NestedFolderSelect } from './folders/NestedFolderSelect';
import { TagsField } from './TagsField';

interface WorkflowSetupDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (
		title: string,
		description: string,
		folderId?: string,
		tags?: Tag[] | string[] // Support both formats for backward compatibility
	) => void | Promise<void>;
	initialTitle?: string;
	initialDescription?: string;
	initialFolderId?: string;
	initialTags?: Tag[] | string[]; // Support both formats for backward compatibility
	isSaving?: boolean;
}

export function WorkflowSetupDialog({
	isOpen,
	onClose,
	onSave,
	initialTitle = '',
	initialDescription = '',
	initialFolderId = '',
	initialTags = [],
	isSaving: externalIsSaving = false,
}: WorkflowSetupDialogProps) {
	const [title, setTitle] = useState(initialTitle);
	const [description, setDescription] = useState(initialDescription);
	const [selectedFolderId, setSelectedFolderId] = useState(initialFolderId);
	const [tags, setTags] = useState<Tag[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	// No need to fetch folders separately - NestedFolderSelect handles this

	// Get tags utilities for conversion
	const { convertLegacyTags } = useTags();

	// Convert initial tags helper function
	const convertInitialTags = (initialTags?: Tag[] | string[]): Tag[] => {
		if (!Array.isArray(initialTags) || initialTags.length === 0) {
			return [];
		}

		// Check if first item is a string to determine format
		if (typeof initialTags[0] === 'string') {
			return convertLegacyTags(initialTags as string[]);
		}

		return initialTags as Tag[];
	};

	// Update state when props change (important for new workflows)
	useEffect(() => {
		setTitle(initialTitle);
		setDescription(initialDescription);
		setSelectedFolderId(initialFolderId);
		setTags(convertInitialTags(initialTags));
	}, [initialTitle, initialDescription, initialFolderId, initialTags, convertLegacyTags]);

	// Reset state when dialog opens/closes
	useEffect(() => {
		if (isOpen) {
			setTitle(initialTitle);
			setDescription(initialDescription);
			setSelectedFolderId(initialFolderId);
			setTags(convertInitialTags(initialTags));
			setIsSaving(false);
		}
	}, [isOpen, initialTitle, initialDescription, initialFolderId, initialTags, convertLegacyTags]);

	if (!isOpen) return null;

	const handleSave = async () => {
		const isCurrentlySaving = isSaving || externalIsSaving;
		if (isCurrentlySaving) return; // Prevent double-clicks
		console.log('Saving workflow...');
		setIsSaving(true);
		try {
			const workflowTitle = title.trim() || 'Untitled Workflow';
			const folderId = selectedFolderId || undefined;
			await onSave(workflowTitle, description.trim(), folderId, tags);
			onClose();
		} catch (error) {
			console.error('Error saving workflow:', error);
			// Close the dialog so the user can see the error dialog
			onClose();
		} finally {
			setIsSaving(false);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			// Prevent default Enter behavior (form submission)
			e.preventDefault();
		} else if (e.key === 'Escape') {
			onClose();
		}
	};

	const handleFormSubmit = (e: React.FormEvent) => {
		e.preventDefault(); // Prevent any form submission
	};

	return (
		<div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70">
			<div className="mx-4 w-full max-w-md bg-white rounded-lg border shadow-xl dark:bg-gray-800 dark:border-gray-700">
				{/* Header */}
				<div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
					<div className="flex gap-3 items-center">
						<div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-lg dark:bg-blue-900/30">
							<FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						<h2 className="text-lg font-semibold text-gray-900 dark:text-white">
							Workflow Setup
						</h2>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="p-1 text-gray-400 rounded transition-colors dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
					>
						<X className="w-5 h-5" />
					</button>
				</div>

				{/* Content */}
				<form onSubmit={handleFormSubmit} className="p-6 space-y-4">
					<p className="text-sm text-gray-600 dark:text-gray-400">
						Give your workflow a meaningful title, description, organize it into a
						folder, and add tags for easy discovery.
					</p>

					{/* Title Input */}
					<div className="space-y-2">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Workflow Title <span className="text-red-500 dark:text-red-400">*</span>
						</label>
						<input
							type="text"
							value={title}
							onChange={e => setTitle(e.target.value)}
							placeholder="Enter workflow title (e.g., Customer Onboarding)"
							className="px-3 py-2 w-full placeholder-gray-500 text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
							onKeyDown={handleKeyDown}
							autoFocus
						/>
					</div>

					{/* Folder Selection */}
					<div className="space-y-2">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Folder (Optional)
						</label>
						<NestedFolderSelect
							value={selectedFolderId}
							onChange={folderId => setSelectedFolderId(folderId || '')}
							placeholder="No folder (organize later)"
							isClearable={true}
						/>
					</div>

					{/* Tags Input */}
					<div className="space-y-2">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Tags (Optional)
						</label>
						<TagsField
							value={tags}
							onChange={setTags}
							maxTags={10}
							placeholder="Start typing to search or create tags (e.g., automation, crm, marketing)"
						/>
					</div>

					{/* Description Input */}
					<div className="space-y-2">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Description (Optional)
						</label>
						<textarea
							value={description}
							onChange={e => setDescription(e.target.value)}
							placeholder="Describe what this workflow does (e.g., Automates new customer registration and welcome email)"
							rows={3}
							className="px-3 py-2 w-full placeholder-gray-500 text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors resize-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
							onKeyDown={handleKeyDown}
						/>
					</div>

					{/* Examples */}
					<div className="p-3 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
						<h4 className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-300">
							💡 Example Workflows
						</h4>
						<div className="space-y-1 text-xs text-blue-800 dark:text-blue-200">
							<div>
								• <strong>Lead Processing:</strong> Capture leads from forms and
								sync to CRM
							</div>
							<div>
								• <strong>Order Fulfillment:</strong> Process orders and send
								notifications
							</div>
							<div>
								• <strong>Customer Support:</strong> Route tickets and update status
							</div>
						</div>
					</div>
				</form>

				{/* Footer */}
				<div className="flex gap-3 justify-end p-6 bg-gray-50 border-t border-gray-200 dark:border-gray-700 dark:bg-gray-800">
					<button
						type="button"
						onClick={onClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
					>
						Skip for Now
					</button>
					<button
						type="button"
						onClick={handleSave}
						disabled={isSaving || externalIsSaving}
						className="px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded-lg transition-colors hover:bg-teal-600 dark:bg-teal-500 dark:hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-teal-500"
					>
						{isSaving || externalIsSaving ? 'Saving...' : 'Continue'}
					</button>
				</div>

				{/* Keyboard Shortcuts */}
				<div className="px-6 pb-4 text-xs text-gray-500 dark:text-gray-400">
					Press{' '}
					<kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded">
						Cmd+Enter
					</kbd>{' '}
					to continue or{' '}
					<kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded">
						Esc
					</kbd>{' '}
					to skip
				</div>
			</div>
		</div>
	);
}
